import type { ReactNode, JSX } from 'react'

import { useSortable } from '@dnd-kit/sortable'
import clsx from 'clsx'

import { GripVerticalIcon, UnstyledButton } from '@buffer-mono/popcorn'

import useDraggingCursor from '~publish/hooks/useDraggingCursor'

import type { MainSidebarChannel } from '../useChannels'

import styles from './DraggableChannelItem.module.css'

interface DraggableChannelItemProps {
  channel: MainSidebarChannel
  disabled?: boolean
  children: (dragHandle: ReactNode) => ReactNode
}

/**
 * Wrapper component that makes a channel item draggable and sortable.
 * Handles drag transforms, drag handle, and visual feedback during drag operations.
 */
export function DraggableChannelItem({
  channel,
  disabled = false,
  children,
}: DraggableChannelItemProps): JSX.Element {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    setActivatorNodeRef,
  } = useSortable({
    id: channel.id,
    disabled,
  })

  useDraggingCursor(isDragging)

  const isDragShadow = isDragging

  const style: React.CSSProperties & Record<`--${string}`, string> = {
    '--translate-x': transform?.x ? `${transform.x}px` : '',
    '--translate-y': transform?.y ? `${transform.y}px` : '',
    '--scale-x': transform?.scaleX ? `${transform.scaleX}` : '',
    '--scale-y': transform?.scaleY ? `${transform.scaleY}` : '',
    transition,
  }

  const dragHandle =
    !disabled && !isDragShadow ? (
      <UnstyledButton
        className={styles.dragHandle}
        {...attributes}
        {...listeners}
        ref={setActivatorNodeRef}
        aria-label={`Drag to reorder ${channel.name}`}
      >
        <GripVerticalIcon />
      </UnstyledButton>
    ) : null

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={clsx(styles.draggableItem, {
        [styles.dragShadow]: isDragShadow,
      })}
    >
      {children(dragHandle)}
    </li>
  )
}
